import axios from 'axios';
import apiClient, { ACCESS_TOKEN_KEY, REFRESH_TOKEN_KEY } from '../api/client';
import {
    LoginResponse,
    User,
    MqttTokenRequest,
    MqttTokenResponse,
} from '../types/auth';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

export const authService = {
    /**
     * Login with username and password
     * Note: The login endpoint expects form-urlencoded data, NOT JSON
     */
    async login(username: string, password: string): Promise<LoginResponse> {
        const formData = new URLSearchParams();
        formData.append('username', username);
        formData.append('password', password);

        const response = await axios.post<LoginResponse>(
            `${API_URL}/api/v1/auth/login`,
            formData.toString(),
            {
                headers: {
                    'Content-Type': 'application/x-www-form-urlencoded',
                },
            }
        );

        const { access_token, refresh_token } = response.data;

        // Store tokens in localStorage
        localStorage.setItem(ACCESS_TOKEN_KEY, access_token);
        localStorage.setItem(REFRESH_TOKEN_KEY, refresh_token);

        return response.data;
    },

    /**
     * Refresh the access token using the refresh token
     */
    async refresh(refreshToken: string): Promise<LoginResponse> {
        const response = await axios.post<LoginResponse>(
            `${API_URL}/api/v1/auth/refresh`,
            { refresh_token: refreshToken },
            {
                headers: {
                    'Content-Type': 'application/json',
                },
            }
        );

        const { access_token, refresh_token: newRefreshToken } = response.data;

        // Store new tokens in localStorage
        localStorage.setItem(ACCESS_TOKEN_KEY, access_token);
        localStorage.setItem(REFRESH_TOKEN_KEY, newRefreshToken);

        return response.data;
    },

    /**
     * Get the current authenticated user's information
     */
    async getCurrentUser(): Promise<User> {
        const response = await apiClient.get<User>('/api/v1/auth/me');
        return response.data;
    },

    /**
     * Preview an invite/registration token so the set-password page can greet
     * the user and detect expired/used links early. Uses bare axios (no auth).
     */
    async validateRegistration(
        token: string
    ): Promise<{ username: string; email: string }> {
        const response = await axios.get<{ username: string; email: string }>(
            `${API_URL}/api/v1/auth/registration`,
            { params: { token } }
        );
        return response.data;
    },

    /**
     * Set an initial password using an invite/registration token (invite flow).
     * Uses bare axios because the user is not authenticated yet.
     */
    async setPassword(token: string, password: string): Promise<void> {
        await axios.post(
            `${API_URL}/api/v1/auth/set-password`,
            { token, password },
            { headers: { 'Content-Type': 'application/json' } }
        );
    },

    /**
     * Request a per-event MQTT JWT for connecting to the RabbitMQ broker.
     * The returned token must be supplied as the MQTT password; broker
     * credentials use literal username "jwt".
     */
    async requestMqttToken(
        eventId: string,
        factionId: string | null
    ): Promise<MqttTokenResponse> {
        const requestBody: MqttTokenRequest = {
            event_id: eventId,
            faction_id: factionId,
        };

        const response = await apiClient.post<MqttTokenResponse>(
            '/api/v1/auth/mqtt-token',
            requestBody
        );

        return response.data;
    },

    /**
     * Logout the user by clearing stored tokens
     */
    logout(): void {
        localStorage.removeItem(ACCESS_TOKEN_KEY);
        localStorage.removeItem(REFRESH_TOKEN_KEY);
    },

    /**
     * Check if there are stored tokens
     */
    hasStoredTokens(): boolean {
        return !!(
            localStorage.getItem(ACCESS_TOKEN_KEY) &&
            localStorage.getItem(REFRESH_TOKEN_KEY)
        );
    },

    /**
     * Get the stored access token
     */
    getAccessToken(): string | null {
        return localStorage.getItem(ACCESS_TOKEN_KEY);
    },

    /**
     * Get the stored refresh token
     */
    getRefreshToken(): string | null {
        return localStorage.getItem(REFRESH_TOKEN_KEY);
    },
};

export default authService;
